import { Autocomplete, Chip, TextField } from '@mui/material';
import { createFilterOptions } from '@mui/material/Autocomplete';
import AddIcon from '@mui/icons-material/Add';
import type { MachineOption } from '../types';

const CREATE_SENTINEL = '__create__';
const NO_LINKED_OPTIONS_SENTINEL = '__no_linked_options__';
const optionFilter = createFilterOptions<string | MachineOption>({
  stringify: (option) => (typeof option === 'string' ? option : option.name),
});

interface OptionsAutocompleteFieldProps {
  // Catalog options to offer — already filtered by the caller (e.g. via
  // useCategoryLinkedOptions) to those linked to the currently selected category.
  options: MachineOption[];
  value: (MachineOption | string)[];
  hasNoLinkedOptions: boolean;
  label: string;
  addOptionLabel: string;
  noLinkedOptionsLabel: string;
  placeholder?: string;
  onChange: (optionIds: string[], freeTextOptions: string[]) => void;
  onCreateNew: () => void;
}

// Shared "Opties / toebehoren" multi-value freeSolo Autocomplete (managed catalog options plus
// free text) with a "+ Nieuwe optie toevoegen" quick-create row and, when the caller reports no
// options linked to the selected category, a disabled explanatory row — used by both
// MachineFormPage (create) and DetailActionDialog (edit) so the two forms can't drift apart.
export default function OptionsAutocompleteField({
  options,
  value,
  hasNoLinkedOptions,
  label,
  addOptionLabel,
  noLinkedOptionsLabel,
  placeholder,
  onChange,
  onCreateNew,
}: OptionsAutocompleteFieldProps) {
  const createSentinel = (): MachineOption => ({
    id: CREATE_SENTINEL,
    name: addOptionLabel,
    organization_id: '',
    archived: false,
    created_at: '',
  });

  return (
    <Autocomplete
      multiple
      freeSolo
      options={options}
      filterOptions={(opts, params) => [
        createSentinel(),
        ...(hasNoLinkedOptions
          ? [{ id: NO_LINKED_OPTIONS_SENTINEL, name: noLinkedOptionsLabel, organization_id: '', archived: false, created_at: '' }]
          : []),
        ...optionFilter(opts, params),
      ]}
      getOptionDisabled={(opt) => typeof opt !== 'string' && opt.id === NO_LINKED_OPTIONS_SENTINEL}
      getOptionLabel={(opt) => (typeof opt === 'string' ? opt : opt.name)}
      isOptionEqualToValue={(opt, val) => typeof opt !== 'string' && typeof val !== 'string' && opt.id === val.id}
      value={value}
      onChange={(_, newValue) => {
        const hasCreateSentinel = newValue.some((v) => typeof v !== 'string' && v.id === CREATE_SENTINEL);
        if (hasCreateSentinel) {
          onCreateNew();
          return;
        }
        const ids = newValue.filter((v): v is MachineOption => typeof v !== 'string').map((v) => v.id);
        const texts = newValue.filter((v): v is string => typeof v === 'string');
        onChange(ids, texts);
      }}
      renderValue={(val, getItemProps) =>
        val.map((option, index) => {
          const optionLabel = typeof option === 'string' ? option : option.name;
          const { key, ...itemProps } = getItemProps({ index });
          return <Chip key={key} label={optionLabel} size="small" {...itemProps} />;
        })
      }
      renderOption={({ key, ...props }, opt) => {
        const isCreate = typeof opt !== 'string' && opt.id === CREATE_SENTINEL;
        const isInfo = typeof opt !== 'string' && opt.id === NO_LINKED_OPTIONS_SENTINEL;
        return (
          <li
            key={key}
            {...props}
            data-testid={typeof opt === 'string' ? undefined : isInfo ? 'no-linked-options-option' : `option-${opt.id}`}
            className={
              isCreate
                ? `${props.className ?? ''} text-primary`
                : isInfo
                  ? `${props.className ?? ''} text-text-secondary`
                  : props.className
            }
          >
            {isCreate && <AddIcon fontSize="small" className="mr-1" />}
            {typeof opt === 'string' ? opt : opt.name}
          </li>
        );
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={value.length === 0 ? placeholder : undefined}
          slotProps={{
            ...params.slotProps,
            htmlInput: { ...params.slotProps.htmlInput, 'aria-label': label },
          }}
        />
      )}
    />
  );
}
